/**
 * L3 — Learner Activity Progress
 * Route: /reports/journeys/:journeyId/learners/:studentUserId
 *
 * Calls:
 *   GET /api/journeys/{journeyId}/learners/{studentUserId}/activity-progress
 *
 * student_user_id is ALWAYS a string — never parseInt'd.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtDate(dt) {
  if (!dt) return null;
  try {
    return new Date(dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
  } catch { return dt; }
}

// ── Type pill ─────────────────────────────────────────────────────────────────
function TypePill({ type }) {
  const map = {
    catalog:     { label: 'Catalog',     bg: '#dbeafe', color: '#1d4ed8' },
    assessment:  { label: 'Assessment',  bg: '#ede9fe', color: '#6d28d9' },
    external:    { label: 'External',    bg: '#f1f5f9', color: '#475569' },
    manual_task: { label: 'Manual Task', bg: '#fef3c7', color: '#b45309' },
  };
  const s = map[type] || { label: type, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: 10, fontWeight: 700, background: s.bg, color: s.color,
      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ activity }) {
  const { type, status, status_color, progress_pct } = activity;

  if (type === 'external') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
        borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#f1f5f9', color: '#64748b',
      }}>
        <ExternalLink size={10} /> External Link
      </span>
    );
  }

  if (type === 'manual_task') {
    const statusKey = (status || '').toLowerCase().replace(/\s+/g, '_');
    const stdMap = {
      completed:   { bg: '#dcfce7', color: '#15803d', label: 'Completed' },
      in_progress: { bg: '#fef3c7', color: '#b45309', label: 'In Progress' },
      not_started: { bg: '#f1f5f9', color: '#64748b', label: 'Not Started' },
    };
    if (stdMap[statusKey]) {
      const s = stdMap[statusKey];
      return (
        <span style={{
          display: 'inline-block', padding: '2px 10px', borderRadius: 12,
          fontSize: 11, fontWeight: 600, background: s.bg, color: s.color,
          whiteSpace: 'nowrap',
        }}>
          {s.label}
        </span>
      );
    }
    // Custom status: use DB color if available
    const bg = status_color || '#e5e7eb';
    const isLight = !bg || ['#fff', '#ffffff', '#f', '#e5e7eb', '#fef'].some(p => bg.toLowerCase().startsWith(p));
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 12,
        fontSize: 11, fontWeight: 600,
        background: bg, color: isLight ? '#1e293b' : '#fff',
        whiteSpace: 'nowrap',
      }}>
        {status || '—'}
      </span>
    );
  }

  // catalog / assessment
  const map = {
    not_started:  { label: 'Not Started',  bg: '#f1f5f9', color: '#64748b' },
    in_progress:  { label: 'In Progress',  bg: '#dbeafe', color: '#1d4ed8' },
    completed:    { label: 'Completed',    bg: '#dcfce7', color: '#15803d' },
    coming_soon:  { label: 'Coming Soon',  bg: '#f8fafc', color: '#94a3b8', italic: true },
  };
  const s = map[status] || map.not_started;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 10px',
      borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color,
      fontStyle: s.italic ? 'italic' : 'normal',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
      {s.label}
      {status === 'in_progress' && progress_pct != null && (
        <span style={{ fontStyle: 'normal', color: '#64748b' }}>· {progress_pct}%</span>
      )}
    </span>
  );
}

// ── Detail cell (date or progress) ────────────────────────────────────────────
function DetailCell({ activity }) {
  const { type, status, progress_pct, completed_at, coming_soon_date, status_updated_at } = activity;

  if (type === 'external') return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;

  if (type === 'manual_task') {
    if (status_updated_at) {
      return <span style={{ fontSize: 11, color: '#64748b' }}>{fmtDate(status_updated_at)}</span>;
    }
    return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
  }

  // catalog / assessment: only show progress bar when progress_pct > 0
  if (status === 'completed' && completed_at) {
    return <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>{fmtDate(completed_at)}</span>;
  }
  if (status === 'completed' && !completed_at) {
    // completed without a date
    return <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>—</span>;
  }
  if (status === 'in_progress' && Number(progress_pct) > 0) {
    const v = Number(progress_pct);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 100 }}>
        <div style={{ flex: 1, height: 5, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${v}%`, height: '100%', background: '#3b82f6', borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', minWidth: 28 }}>{v}%</span>
      </div>
    );
  }
  // in_progress with progress_pct === 0 (assessment starter) → dash, not 0%
  if (status === 'coming_soon' && coming_soon_date) {
    return <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>From {fmtDate(coming_soon_date)}</span>;
  }
  return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={{ padding: '32px 0' }}>
      {[1, 2].map(ph => (
        <div key={ph} style={{ marginBottom: 32 }}>
          <div style={{ height: 20, width: 220, background: '#e2e8f0', borderRadius: 6, marginBottom: 16 }} />
          {[1, 2, 3].map(r => (
            <div key={r} style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
              <div style={{ height: 14, width: 40, background: '#f1f5f9', borderRadius: 4 }} />
              <div style={{ height: 14, flex: 1, background: '#f1f5f9', borderRadius: 4 }} />
              <div style={{ height: 14, width: 80, background: '#f1f5f9', borderRadius: 4 }} />
              <div style={{ height: 14, width: 90, background: '#f1f5f9', borderRadius: 4 }} />
              <div style={{ height: 14, width: 80, background: '#f1f5f9', borderRadius: 4 }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── CSV export ────────────────────────────────────────────────────────────────
function buildCsv(learner, journey, phases) {
  const header = ['Learner Name', 'Partner', 'Phase', 'Step', 'Activity Name', 'Type', 'Status', 'Progress %', 'Completion Date'];
  const rows = [];
  for (const ph of phases) {
    for (const step of ph.steps || []) {
      for (const act of step.activities || []) {
        const progress = act.type === 'manual_task' ? '' : (act.progress_pct ?? '');
        const date = act.type === 'manual_task'
          ? (act.status_updated_at || '')
          : (act.completed_at || act.coming_soon_date || '');
        rows.push([
          `"${learner?.name || ''}"`,
          `"${learner?.partner_name || ''}"`,
          `"${ph.phase_name || ''}"`,
          step.step_number,
          `"${act.activity_name || ''}"`,
          act.type,
          `"${act.status || ''}"`,
          progress,
          `"${fmtDate(date) || ''}"`,
        ].join(','));
      }
    }
  }
  return [header.join(','), ...rows].join('\n');
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function LearnerActivityPage() {
  const { journeyId, studentUserId } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axios.get(
        `${API}/api/journeys/${journeyId}/learners/${studentUserId}/activity-progress`,
        { withCredentials: true },
      );
      setData(res);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      if (status === 403) setError('You do not have access to this journey report.');
      else if (status === 404) {
        const msg = e?.response?.data?.detail || '';
        setError(msg.toLowerCase().includes('learner') ? 'Learner not found in the system.' : 'Journey not found.');
      } else {
        setError('Unable to load activity data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [journeyId, studentUserId, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!data) return;
    const csv = buildCsv(data.learner, data.journey, data.phases || []);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    const name = data.learner?.name || studentUserId;
    a.href = url;
    a.download = `activity_${journeyId}_${studentUserId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const learner  = data?.learner  || {};
  const journey  = data?.journey  || {};
  const phases   = data?.phases   || [];

  const enrollmentStatus = journey.enrollment_status;

  // Compute required-only progress client-side (App 1 overall_progress_pct is unreliable / always 0)
  const _allActs     = phases.flatMap(p => (p.steps || []).flatMap(s => s.activities || []));
  const _reqActs     = _allActs.filter(a => !a.is_optional);
  const reqTotal     = _reqActs.length;
  const reqCompleted = _reqActs.filter(a => a.status === 'completed').length;
  const reqInProg    = _reqActs.filter(a => a.status === 'in_progress').length;
  const reqNotStart  = reqTotal - reqCompleted - reqInProg;
  const reqPct       = reqTotal > 0 ? Math.round((reqCompleted / reqTotal) * 100) : 0;
  const reqIpPct     = reqTotal > 0 ? Math.round((reqInProg    / reqTotal) * 100) : 0;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Back nav */}
      <button
        data-testid="back-to-journey-detail-btn"
        onClick={() => navigate(`/reports/journeys/${journeyId}`)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: '#6366f1', fontWeight: 600, padding: 0,
        }}
      >
        <ArrowLeft size={14} /> Back to Journey
      </button>

      {loading ? (
        <Skeleton />
      ) : error ? (
        <div
          data-testid="activity-error"
          style={{
            background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
            borderRadius: 8, padding: '16px 20px', fontSize: 14,
          }}
        >
          {error}
        </div>
      ) : (
        <>
          {/* Header card */}
          <div
            data-testid="learner-activity-header"
            style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '18px 24px', marginBottom: 24,
              display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
            }}
          >
            {/* Left: learner */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                  {learner.name || (
                    <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: 14 }}>
                      LMS ID: {learner.student_user_id || studentUserId}
                    </span>
                  )}
                </span>
                {enrollmentStatus && (
                  <span style={{
                    padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: enrollmentStatus === 'Completed' ? '#dcfce7' : enrollmentStatus === 'In Progress' ? '#dbeafe' : '#f1f5f9',
                    color: enrollmentStatus === 'Completed' ? '#15803d' : enrollmentStatus === 'In Progress' ? '#1d4ed8' : '#64748b',
                  }}>
                    {enrollmentStatus}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>LMS ID: <strong style={{ color: '#374151', fontFamily: 'monospace' }}>{learner.student_user_id || studentUserId}</strong></span>
                {learner.partner_name && <span>Partner: <strong style={{ color: '#374151' }}>{learner.partner_name}</strong></span>}
                {learner.email && <span>Email: <strong style={{ color: '#374151' }}>{learner.email}</strong></span>}
              </div>
            </div>

            {/* Right: journey + overall progress */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                {journey.name || '—'}
              </div>
              {reqTotal > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <div style={{ width: 120, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${reqPct}%`, height: '100%', background: reqPct >= 100 ? '#22c55e' : reqPct >= 50 ? '#3b82f6' : '#f59e0b', borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{reqPct}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Export button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button
              data-testid="activity-export-btn"
              onClick={handleExport}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 7,
                background: '#fff', cursor: 'pointer', fontSize: 12, color: '#217346', fontWeight: 600,
              }}
            >
              <Download size={13} /> Export CSV
            </button>
          </div>

          {/* Journey progress summary */}
          {reqTotal > 0 && (() => {
            const completed  = reqCompleted;
            const inProgress = reqInProg;
            const notStarted = reqNotStart;
            const pct        = reqPct;
            const ipPct      = reqIpPct;
            return (
              <div
                data-testid="journey-progress-summary"
                className="report-card"
                style={{ marginBottom: 20, padding: '14px 20px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  {/* Main fraction — required activities only */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: '#15803d', lineHeight: 1 }}>
                      {completed}
                    </span>
                    <span style={{ fontSize: 15, color: '#94a3b8', fontWeight: 600 }}>
                      / {reqTotal} required completed
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                      <div style={{ width: `${pct}%`, background: '#22c55e', transition: 'width .4s ease' }} />
                      <div style={{ width: `${ipPct}%`, background: '#93c5fd', transition: 'width .4s ease' }} />
                    </div>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>{pct}% required complete</span>
                  </div>

                  {/* Stat chips */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Completed',   count: completed,  bg: '#dcfce7', color: '#15803d' },
                      { label: 'In Progress', count: inProgress, bg: '#dbeafe', color: '#1d4ed8' },
                      { label: 'Not Started', count: notStarted, bg: '#f1f5f9', color: '#64748b' },
                    ].map(({ label, count, bg, color }) => (
                      <span
                        key={label}
                        data-testid={`summary-stat-${label.toLowerCase().replace(' ', '-')}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 10px', borderRadius: 20,
                          fontSize: 11, fontWeight: 600, background: bg, color,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                        {count} {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Phases */}
          {phases.length === 0 ? (
            <div
              data-testid="no-phases-message"
              style={{
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: 14,
              }}
            >
              No phase data available for this journey.
            </div>
          ) : (
            <div data-testid="activity-phases">
              {phases.map((phase, pi) => {
                // Flatten all activities across steps for this phase
                const stepRows = [];
                for (const step of phase.steps || []) {
                  if (!step.activities || step.activities.length === 0) continue;
                  for (const act of step.activities) {
                    stepRows.push({ step, act });
                  }
                }
                if (stepRows.length === 0) return null;

                return (
                  <div key={phase.phase_id || pi} data-testid={`phase-section-${pi}`} style={{ marginBottom: 32 }}>
                    {/* Phase header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
                    }}>
                      <div style={{
                        background: '#6366f1', color: '#fff', borderRadius: 6,
                        padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        Phase {phase.phase_order}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                        {phase.phase_name}
                      </div>
                      {/* Fix 3: required-only progress in phase header */}
                      {phase.required_total > 0 && (
                        <div
                          data-testid={`phase-required-progress-${pi}`}
                          style={{
                            fontSize: 11, color: '#64748b', fontWeight: 600,
                            background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: 20, padding: '2px 10px', whiteSpace: 'nowrap',
                          }}
                        >
                          <span style={{ color: '#15803d', fontWeight: 700 }}>
                            {phase.required_completed}
                          </span>
                          /{phase.required_total} required completed
                        </div>
                      )}
                      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                    </div>

                    {/* Activities table */}
                    <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="report-table" data-testid={`phase-table-${pi}`}>
                          <thead>
                            <tr>
                              <th style={{ width: 50, textAlign: 'center' }}>Step</th>
                              <th>Activity</th>
                              <th style={{ width: 110 }}>Type</th>
                              <th style={{ width: 180 }}>Status</th>
                              <th style={{ minWidth: 140 }}>Date / Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stepRows.map(({ step, act }, ri) => (
                              <tr
                                key={`${step.step_id || step.step_number}-${ri}`}
                                data-testid={`activity-row-${pi}-${ri}`}
                                style={{
                                  opacity: !step.is_live ? 0.6 : 1,
                                }}
                              >
                                <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                                  {step.step_number}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>
                                      {act.activity_name || '—'}
                                    </span>
                                    {act.type === 'external' && act.external_url && (
                                      <a
                                        href={act.external_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#6366f1', display: 'inline-flex', alignItems: 'center' }}
                                        title="Open external resource"
                                      >
                                        <ExternalLink size={11} />
                                      </a>
                                    )}
                                    {!step.is_live && step.coming_soon_date && (
                                      <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                                        (Coming {fmtDate(step.coming_soon_date)})
                                      </span>
                                    )}
                                  </div>
                                  {/* Fix 2: column heading + Required/Optional badge */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                                    {act.column_heading && (
                                      <span style={{ fontSize: 10, color: '#94a3b8' }}>
                                        {act.column_heading}
                                      </span>
                                    )}
                                    <span
                                      data-testid={`badge-required-${act.is_optional ? 'optional' : 'required'}`}
                                      style={{
                                        fontSize: 9, fontWeight: 700, padding: '1px 6px',
                                        borderRadius: 20, letterSpacing: '0.03em', whiteSpace: 'nowrap',
                                        background: act.is_optional ? '#f1f5f9' : '#fef3c7',
                                        color:      act.is_optional ? '#64748b' : '#92400e',
                                      }}
                                    >
                                      {act.is_optional ? 'Optional' : 'Required'}
                                    </span>
                                  </div>
                                </td>
                                <td><TypePill type={act.type} /></td>
                                <td><StatusBadge activity={act} /></td>
                                <td><DetailCell activity={act} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
